import { getOrientation } from 'get-orientation/browser';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { FiCamera, FiRotateCcw, FiRotateCw } from 'react-icons/fi';
import { useMutation } from 'react-query';

import { Button, InputSlider, Loader, Modal } from '@/components';
import {
  getCroppedImg,
  getRotatedImage,
} from '@/components/ReactEasyCrop/CanvasUtils';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import { saveProfilePic } from '@/services/editAccount';
import { ResponseSaveProfilePic } from '@/types/editProfile';
import { cn, COLORS, formatFileSize, nameAvatar } from '@/utils';

const MAX_FILE_SIZE_KB = 2000;
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif'] as const;

const ORIENTATION_TO_ANGLE = {
  '3': 180,
  '6': 90,
  '8': -90,
} as const;

const INITIAL_CROP = { x: 0, y: 0 };
const INITIAL_CROPPED_AREA: Area = {
  width: 0,
  height: 0,
  x: 0,
  y: 0,
};

export const ProfilePicture = () => {
  const { userInitialData, setUserInitialData } = useAuth();
  const toast = useToast();
  const { isDesktop } = useIsDesktop();

  const [imageToCropped, setImageToCropped] = useState('');
  const [crop, setCrop] = useState(INITIAL_CROP);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [showModalConfirm, setShowModalConfirm] = useState(false);

  const [croppedAreaPixels, setCroppedAreaPixels] =
    useState<Area>(INITIAL_CROPPED_AREA);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const userInitialDataRef = useRef(userInitialData);
  const croppedBlobRef = useRef<Blob | null>(null);
  const objectUrlRef = useRef<string>('');

  useEffect(() => {
    userInitialDataRef.current = userInitialData;
  });

  const isPremiumWithoutImage =
    Boolean(userInitialData?.premium) && !userInitialData?.pathImagenPerfil;

  const resetEditorState = useCallback(() => {
    setImageToCropped('');
    setCrop(INITIAL_CROP);
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(INITIAL_CROPPED_AREA);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const onCropComplete = useCallback((_croppedArea: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const {
    isLoading,
    mutateAsync: requestSavePic,
    reset: resetMutation,
    isSuccess,
  } = useMutation(
    'saveProfilePic',
    async (image: Blob) => saveProfilePic(image),
    {
      onSuccess: (response: ResponseSaveProfilePic) => {
        if (!response.estado) {
          toast.show(
            'Algo salió mal mientras intentabas guardar la foto de perfil'
          );
          return;
        }

        const currentUserData = userInitialDataRef.current;

        if (currentUserData && response.pathImagenPerfil) {
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
          }
          const displayUrl = croppedBlobRef.current
            ? URL.createObjectURL(croppedBlobRef.current)
            : response.pathImagenPerfil;
          croppedBlobRef.current = null;
          setUserInitialData({
            ...currentUserData,
            pathImagenPerfil: displayUrl,
            fotoPerfil: displayUrl,
          });
        } else {
          console.error(
            '[PP] SKIP setUserInitialData — currentUserData:',
            Boolean(currentUserData),
            'pathImagenPerfil:',
            response?.pathImagenPerfil
          );
        }

        resetEditorState();
      },
      onError: (err) => {
        const error = err as Error;
        console.error('[PP] onError', error.message);
        toast.showError(error);
      },
      onSettled: () => {
        resetMutation();
      },
    }
  );

  const validateFile = useCallback(
    (file: File) => {
      const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;

      if (
        !ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number])
      ) {
        toast.show(
          "Sólo se permiten archivos de extensión '.jpg', '.jpeg', '.png' o '.gif'."
        );
        return false;
      }

      if (file.size > MAX_FILE_SIZE_KB * 1000) {
        toast.show(
          `El tamaño del archivo debe ser menor que ${formatFileSize(
            MAX_FILE_SIZE_KB * 1000
          )}. Por favor, ingresalo nuevamente.`
        );
        return false;
      }

      return true;
    },
    [toast]
  );

  const onFileChange = useCallback(
    async (target: HTMLInputElement | null | undefined) => {
      if (!target?.files?.length) return;

      try {
        const file = target.files[0];

        if (!validateFile(file)) return;

        let imageDataUrl = await readFile(file);

        try {
          const orientation = await getOrientation(file);
          const rotationFromExif =
            ORIENTATION_TO_ANGLE[
              String(orientation) as keyof typeof ORIENTATION_TO_ANGLE
            ];

          if (rotationFromExif) {
            imageDataUrl = await getRotatedImage(
              imageDataUrl,
              rotationFromExif
            );
          }
        } catch (e) {
          console.error('failed to detect the orientation', e);
        }

        setCrop(INITIAL_CROP);
        setZoom(1);
        setRotation(0);
        setCroppedAreaPixels(INITIAL_CROPPED_AREA);
        setImageToCropped(imageDataUrl);
      } finally {
        target.value = '';
      }
    },
    [validateFile]
  );

  const savePic = useCallback(async () => {
    try {
      if (!imageToCropped || isLoading) return;

      const croppedImage = await getCroppedImg(
        imageToCropped,
        croppedAreaPixels,
        {
          rotation,
          outputType: 'image/jpeg',
          quality: 0.5,
        }
      );

      if (!croppedImage) {
        toast.show('No se pudo generar la imagen recortada');
        return;
      }

      croppedBlobRef.current = croppedImage;
      await requestSavePic(croppedImage);
    } catch (e) {
      console.error(e);
      toast.show('Hubo un problema al procesar la imagen');
    }
  }, [
    croppedAreaPixels,
    imageToCropped,
    isLoading,
    requestSavePic,
    rotation,
    toast,
  ]);

  const onClose = useCallback(() => {
    resetMutation();
    resetEditorState();
  }, [resetEditorState, resetMutation]);

  const rotateLeft = useCallback(() => setRotation((prev) => prev - 90), []);
  const rotateRight = useCallback(() => setRotation((prev) => prev + 90), []);

  const avatarFallback = useMemo(() => {
    return userInitialData?.nombre_dt
      ? nameAvatar(userInitialData.nombre_dt)
      : '';
  }, [userInitialData?.nombre_dt]);

  const cropSizeByDevices = !isDesktop
    ? { width: 333, height: 333 }
    : { width: 182, height: 182 };

  return (
    <>
      <Loader isVisible={isLoading && !isSuccess} />

      <div
        className="lg:mb-7 relative w-[131px] mx-auto lg:mx-0 cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          accept=".jpg,.jpeg,.png,.gif"
          style={{ display: 'none' }}
          type="file"
          onChange={(e) => onFileChange(e.target)}
        />

        <figure
          className={cn(
            'relative max-w-[131px] min-w-[131px] h-[131px] bg-gdt-blue-xl2 text-gdt-blue-header flex items-center justify-center border-white border-2 overflow-hidden rounded-full',
            {
              'border-gdt-status-yellow text-gdt-status-yellow bg-gdt-status-yellow-light':
                isPremiumWithoutImage,
            }
          )}
        >
          {userInitialData?.pathImagenPerfil ? (
            <img
              key={userInitialData?.pathImagenPerfil}
              alt="Foto de perfil"
              className="w-full"
              src={userInitialData?.pathImagenPerfil}
            />
          ) : (
            <figcaption className="text-[41px]">{avatarFallback}</figcaption>
          )}
        </figure>

        <button
          aria-label="Cambiar foto de perfil"
          className="w-8 h-8 bg-gdt-blue rounded-full flex items-center justify-center absolute bottom-2 right-0 border-white border-2"
          type="button"
        >
          <FiCamera color="white" size={17} />
        </button>
      </div>

      <Modal
        bottomSheetClassName="max-md:px-0"
        className="pt-0 lg:px-6 px-0 pb-8 !max-w-[450px] !w-full"
        closeOutside={false}
        controlledOnClose={() => setShowModalConfirm(true)}
        disableDrag={true}
        isControlledOpen={Boolean(imageToCropped)}
        render={() => {
          return (
            <>
              <p className="font-bold text-base text-black text-center mt-0 mb-6 lg:my-6">
                Editar foto de perfil
              </p>
              <div className="relative w-full h-[360px] lg:h-[223px] bg-gdt-gray-7">
                <Cropper
                  aspect={1}
                  crop={crop}
                  cropShape="round"
                  cropSize={cropSizeByDevices}
                  image={imageToCropped}
                  restrictPosition={false}
                  rotation={rotation}
                  showGrid={false}
                  zoom={zoom}
                  zoomSpeed={0.1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onRotationChange={setRotation}
                  onZoomChange={setZoom}
                />
              </div>

              <div className="mx-5 lg:mx-0">
                <div className="flex w-full justify-between items-center px-2 lg:px-0">
                  <div className="flex-1 items-center justify-between -mt-5 lg:ml-2">
                    <InputSlider
                      backgroundColor={COLORS['gdt-blue'].header}
                      foregroundColor={COLORS['gdt-gray'][5]}
                      maxInputRangeAmmount={3}
                      minInputRangeAmmount={1}
                      showThumb={false}
                      step={0.001}
                      values={[zoom]}
                      onChange={(e) => setZoom(Number(e.join()))}
                    />
                    <div className="flex justify-between w-[107%] -left-[3.5%] relative items-center text-xs text-gdt-gray-4 top-[-5px]" />
                  </div>

                  <div className="w-[45px] lg:w-[85px] flex justify-end lg:justify-center items-center space-x-4 pt-2">
                    {isDesktop && (
                      <button
                        aria-label="Rotar izquierda"
                        type="button"
                        onClick={rotateLeft}
                      >
                        <FiRotateCcw
                          color={COLORS['gdt-blue'].DEFAULT}
                          size={20}
                        />
                      </button>
                    )}
                    <button
                      aria-label="Rotar derecha"
                      type="button"
                      onClick={rotateRight}
                    >
                      <FiRotateCw
                        color={COLORS['gdt-blue'].DEFAULT}
                        size={20}
                      />
                    </button>
                  </div>
                </div>

                <span className="text-xs text-gdt-gray-4">Zoom</span>
                <div className="flex justify-around my-6 space-x-2">
                  <Button
                    aria-label="Cancelar guardado"
                    className="flex-1"
                    type="button"
                    variant="secondary"
                    onClick={() => setShowModalConfirm(true)}
                  >
                    Cancelar
                  </Button>

                  <Button
                    aria-label="Guardar foto de perfil"
                    className="flex-1"
                    disabled={isLoading}
                    type="button"
                    variant="primary"
                    onClick={savePic}
                  >
                    Guardar foto
                  </Button>
                </div>
              </div>
            </>
          );
        }}
        size="xs"
        variant="controlled"
      />

      <Modal
        className="lg:px-6 px-0 pb-8 !max-w-[450px] !w-full"
        controlledOnClose={() => setShowModalConfirm(false)}
        disableDrag={true}
        isControlledOpen={showModalConfirm}
        render={() => {
          return (
            <div className="flex justify-around my-6 space-x-2">
              <Button
                aria-label="Cancelar guardado"
                className="flex-1"
                type="button"
                variant="secondary"
                onClick={() => {
                  onClose();
                  setShowModalConfirm(false);
                }}
              >
                No
              </Button>

              <Button
                aria-label="Revisar foto de perfil"
                className="flex-1"
                type="button"
                variant="primary"
                onClick={() => setShowModalConfirm(false)}
              >
                Si
              </Button>
            </div>
          );
        }}
        showCloseButton={false}
        size="xs"
        title="¿Querés revisar tus datos?"
        variant="controlled"
      />
    </>
  );
};

function readFile(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener(
      'load',
      () => resolve(reader.result as string),
      false
    );

    reader.addEventListener(
      'error',
      () => reject(new Error('No se pudo leer el archivo')),
      false
    );

    reader.readAsDataURL(file);
  });
}
